// Package imports:
import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts/highstock';
import ISLocale from 'date-fns/locale/is';
import { format } from 'date-fns';
// Component imports:
import DisplayBox from '../../DisplayBox/DisplayBox';
// Service imports:
import { formatNumber, getAPItoday } from '../../../services/utils';
import { useApiLmdData } from '../../../services/apiHooks';
import { SHORT_MONTHS } from '../../../services/config';
// Type imports:
import { IDefaultProps } from '../../../types/Types';
import { IApiHistoryTimeseriesShortDATA, IApiLmdSpecialFundsChart } from '../../../types/MarketTypes';

interface IChartDatum {
    x: number | undefined
    y: number | undefined
};

interface IChartDataInfo {
    name: string,
    data: IChartDatum[]
};


interface IOwnProps {
    symbol?: string,
    title?: string,
    height?: number
};

type Props = IOwnProps & IDefaultProps & React.HTMLAttributes<HTMLDivElement>;

const SpecialFundsChart: React.FC<Props> = ({
    symbol,
    title,
    height = 500,
    accessToken,
    refreshRateMs,
    ...props
}) => {
    const [apiDataStyringA] = useApiLmdData<IApiHistoryTimeseriesShortDATA>(
        `/v1/market_data/v1/funds/isl_styring_a/history_timeseries/?from_date=1972-02-26&to_date=${getAPItoday()}`,
        accessToken,
        refreshRateMs
    );
    const [apiDataStyringB] = useApiLmdData<IApiHistoryTimeseriesShortDATA>(
        `/v1/market_data/v1/funds/isl_styring_b/history_timeseries/?from_date=1972-02-26&to_date=${getAPItoday()}`,
        accessToken,
        refreshRateMs
    );
    const [apiDataStyringC] = useApiLmdData<IApiHistoryTimeseriesShortDATA>(
        `/v1/market_data/v1/funds/isl_styring_c/history_timeseries/?from_date=1972-02-26&to_date=${getAPItoday()}`,
        accessToken,
        refreshRateMs
    );
    const [apiDataStyringD] = useApiLmdData<IApiHistoryTimeseriesShortDATA>(
        `/v1/market_data/v1/funds/isl_styring_d/history_timeseries/?from_date=1972-02-26&to_date=${getAPItoday()}`,
        accessToken,
        refreshRateMs
    );
    const [selectedRange, setSelectedRange] = useState(6);

    const handleChartData = (body: IApiHistoryTimeseriesShortDATA) => {
        let dataToReturn: IApiLmdSpecialFundsChart[] = [];
        for(let datum of body.data){
            dataToReturn.push({
                value_date: datum[0],
                lastprice: datum[1],
            })
        }
        return dataToReturn;
    }
    
    const divRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        //STYRING A 
        const chartDataInfoStyringA: IChartDataInfo = {
            name: '',
            data: []
        }

        if (apiDataStyringA.data !== null && !(apiDataStyringA.error instanceof Error)) {
            chartDataInfoStyringA.name = 'Stýring A';

            for (let datum of handleChartData(apiDataStyringA.data)) {
                chartDataInfoStyringA.data.push({
                    x: datum.value_date ?? undefined,
                    y: datum.lastprice ?? undefined
                });
            }
        }

        //STYRING B
        const chartDataInfoStyringB: IChartDataInfo = {
            name: '',
            data: []
        }

        if (apiDataStyringB.data !== null && !(apiDataStyringB.error instanceof Error)) {
            chartDataInfoStyringB.name = 'Stýring B';

            for (let datum of handleChartData(apiDataStyringB.data)) {
                chartDataInfoStyringB.data.push({
                    x: datum.value_date ? datum.value_date : undefined,
                    y: datum.lastprice ?? undefined
                });
            }
        }

        //STYRING C
        const chartDataInfoStyringC: IChartDataInfo = {
            name: '',
            data: []
        }

        if (apiDataStyringC.data !== null && !(apiDataStyringC.error instanceof Error)) {
            chartDataInfoStyringC.name = 'Stýring C';

            for (let datum of handleChartData(apiDataStyringC.data)) {
                chartDataInfoStyringC.data.push({
                    x: datum.value_date ? datum.value_date : undefined,
                    y: datum.lastprice ?? undefined
                });
            }
        }

        //STYRING D
        const chartDataInfoStyringD: IChartDataInfo = {
            name: '',
            data: []
        }

        if (apiDataStyringD.data !== null && !(apiDataStyringD.error instanceof Error)) {
            chartDataInfoStyringD.name = 'Stýring D';

            for (let datum of handleChartData(apiDataStyringD.data)) {
                chartDataInfoStyringD.data.push({
                    x: datum.value_date ? datum.value_date : undefined,
                    y: datum.lastprice ?? undefined
                });
            }
        }
        

        Highcharts.setOptions({
            lang: {
                shortMonths: SHORT_MONTHS,
                resetZoom: 'Til baka',
                resetZoomTitle: ''
            }
        });

        if (divRef.current !== null) {
            Highcharts.stockChart(divRef.current, {
                chart: {
                    height: height,
                    marginTop: 80,
                    reflow: true,
                    style: {
                        fontFamily: "Roboto"
                    },
                    zoomType: "xy",
                    selectionMarkerFill: '#4569EE18',
                    resetZoomButton: {
                        position: {
                            x: 0,
                            y: -75
                        },
                        theme: {
                            r: 6,
                            height: 28
                        }
                    },
                    plotBackgroundColor: {
                        linearGradient: {x1: 0, x2: 0, y1: 0, y2: 1},
                        stops: [
                            [0.05, '#FFF'],
                            [0.95, '#F8F8FA']
                        ]
                    }
                },
                exporting: {
                    buttons: {
                        contextButton: {
                            x: 30,
                            y: 36
                        }
                    }
                },
                credits: {
                    enabled: false
                },
                title: {
                    text: '',
                    align: 'center',
                    x: 60,
                    margin: -14
                },
                navigator: {
                    enabled: true,
                    adaptToUpdatedData: false,
                    handles: {
                        height: 22,
                        width: 6
                    },
                    maskFill: '#D4DEFF80',
                    series: {
                        lineWidth: 0,
                        opacity: 0
                    },
                    height: 20,
                    xAxis: {
                        labels: {
                            style: {
                                color: '#7B7C83',
                                fontSize: '10px'
                            },
                            y: -6
                        },
                        gridLineWidth: 0,
                        minTickInterval: 1000*60*60*24*365,
                        lineWidth: 0
                    },
                    yAxis: {
                        lineWidth: 0
                    },
                    outlineWidth: 0
                },
                scrollbar: {
                    enabled: false
                },
                tooltip: {
                    enabled: true,
                    formatter: function() {
                        //filtera einungis það sem er visible af því ef það er notað points[0] crashar allt þegar eitthvað series er disable-að
                        return (`<div class="chart-tooltip">
                                    <p class="tooltip-date">${format(this.x, 'EEEE, dd. MMM yyyy', {locale: ISLocale})}</p>
                                    ${this.points?.filter(x => x.point.visible === true).map(x => (
                                        `<p><span style="color: ${x.color}">${x.series.getName()}</span>: ${formatNumber(x.y, '-', 1)}</p>`
                                    ))}
                                </div>`).replace(/>,</g,'><')
                    },
                    borderWidth: 0,
                    shadow: false,
                    useHTML: true,
                    shared: true
                },
                xAxis: [{
                    type: 'datetime',
                    title: {
                        text: null
                    },
                    minTickInterval: 1000*60*60*24*30,
                    tickWidth: 0
                }],
                yAxis: [{
                    title: {
                        text: 'Verð',
                        offset: 52,
                        x: 5
                    },
                    minTickInterval: 1,
                    gridLineDashStyle: 'Dash',
                    gridLineWidth: 1,
                    gridLineColor: '#D3D3D6',
                    opposite: false,
                    labels: {
                        x: -11,
                        y: 2
                    }
                }],
                plotOptions: {
                    series: {
                        color: '#4569EE',
                        enableMouseTracking: true,
                        marker: {
                            enabled: false
                        },
                        animation: false,
                        turboThreshold: 0
                    }
                },
                legend: {
                    enabled: true,
                    align: 'left',
                    verticalAlign: 'top',
                    itemStyle: {
                        color: '#232530',
                        fontSize: '14px',
                        fontWeight: '400',
                        lineHeight: '26px'
                    },
                    x: 5,
                    y: 10
                },
                rangeSelector: {
                    allButtonsEnabled: true,
                    buttonPosition: {
                        align: 'right',
                        x: 0,
                        y: -80
                    },
                    buttons: [{
                        type: 'month',
                        count: 1,
                        text: '1 mán.',
                        title: '1 mánuður',
                        events: {
                            click: () => setSelectedRange(0)
                        }
                    }, {
                        type: 'month',
                        count: 3,
                        text: '3 mán.',
                        title: '3 mánuðir',
                        events: {
                            click: () => setSelectedRange(1)
                        }
                    }, {
                        type: 'month',
                        count: 6,
                        text: '6 mán.',
                        title: '6 mánuðir',
                        events: {
                            click: () => setSelectedRange(2)
                        }
                    }, {
                        type: 'ytd',
                        text: 'Þetta ár',
                        title: 'Þetta ár',
                        events: {
                            click: () => setSelectedRange(3)
                        }
                    }, {
                        type: 'year',
                        count: 1,
                        text: '1 ár',
                        title: '1 ár',
                        events: {
                            click: () => setSelectedRange(4)
                        }
                    }, {
                        type: 'year',
                        count: 3,
                        text: '3 ár',
                        title: '3 ár',
                        events: {
                            click: () => setSelectedRange(5)
                        }
                    }, {
                        type: 'year',
                        count: 5,
                        text: '5 ár',
                        title: '5 ár',
                        events: {
                            click: () => setSelectedRange(6)
                        }
                    }],
                    buttonSpacing: 5,
                    buttonTheme: {
                        r: 6,
                        height: 25,
                        width: 50
                    },
                    inputEnabled: false,
                    selected: selectedRange,
                    labelStyle: {
                        display: 'none'
                    },
                    dropdown: 'never'
                },
                responsive: {
                    rules: [{
                        condition: {
                            maxWidth: 369
                        },
                        chartOptions: {
                            rangeSelector: {
                                dropdown: 'always'
                            }
                        }
                    }, {
                        condition: {
                           callback: function() {
                               return window.matchMedia('(max-width: 767px)').matches;
                           }
                        },
                        chartOptions: {
                            chart: {
                                spacingLeft: 0,
                                spacingRight: 5,
                                marginTop: 60
                            },
                            legend: {
                                x: 10,
                                y: -10,
                                itemStyle: {
                                    fontSize: '12px'
                                }
                            },
                            navigator: {
                                margin: 13
                            },
                            rangeSelector: {
                                buttonPosition: {
                                    y: -60
                                },
                                buttonTheme: {
                                    width: 36,
                                    height: 24
                                }
                            },
                            xAxis: {
                                labels: {
                                    style: {
                                        fontSize: '9px'
                                    }
                                }
                            },
                            yAxis: {
                                labels: {
                                    style: {
                                        fontSize: '9px'
                                    },
                                    x: -1
                                },
                                title: {
                                    offset: 27,
                                    x: 0,
                                    style: {
                                        fontSize: '10px'
                                    }
                                }
                            }
                        }
                    }]
                },
                series: [{
                    type: 'line',
                    id: 'A',
                    name: 'Stýring A',
                    data: chartDataInfoStyringA.data,
                    color: '#4569EE'
                }, 
                {
                    type: 'line',
                    id: 'B',
                    name: 'Stýring B',
                    data: chartDataInfoStyringB.data,
                    color: '#FF7697',
                },
                {
                    type: 'line',
                    id: 'C',
                    name: 'Stýring C',
                    data: chartDataInfoStyringC.data,
                    color: '#48A85D',
                },
                {
                    type: 'line',
                    id: 'D',
                    name: 'Stýring D',
                    data: chartDataInfoStyringD.data,
                    color: '#6CD3FF',
                }
            ]
            }, () => {});
        }
    }, [ apiDataStyringA, apiDataStyringB, apiDataStyringC, apiDataStyringD, divRef.current]);

    return (
        <DisplayBox 
            className='KCL_chart'
            title={title}
            {...props}
        >
            <div className="chart" ref={divRef}></div>
        </DisplayBox>
    );
}

export default SpecialFundsChart;